import { useCallback, useState } from 'react';
import { IInventoryOutput, IProduct } from '../../../shared/interfaces';
import { useToast } from '../../../hooks/toast';
import {
  FormOutlined,
  ShoppingOutlined,
  CalendarOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { AInput, AModal, ASelect } from '../../../components';
import { Col, Form, Row, Card, Space, Typography } from 'antd';
import { schemaInventoryOutput } from '../schemas/yup.inventory-output.schema';
import api from '../../../services/api';

const { Text } = Typography;

interface IInventoryOutputProps {
  open: boolean;
  close: () => void;
  setOutputs: React.Dispatch<React.SetStateAction<IInventoryOutput[]>>;
  products: IProduct[];
}

const InventoryOutputCreate: React.FC<IInventoryOutputProps> = ({
  open,
  close,
  setOutputs,
  products,
}) => {
  const { addToast } = useToast();
  const [date, setDate] = useState<string>('');
  const [productId, setProductId] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(0);
  const [observation, setObservation] = useState<string>('');

  const handleCreate = useCallback(() => {
    schemaInventoryOutput
      .validate({
        date: date !== '' ? date : null,
        product_id: productId,
        quantity,
        observation,
      })
      .then(async (validated) => {
        await api
          .post(`/inventory-outputs/`, validated)
          .then((response) => {
            setOutputs((state: IInventoryOutput[]) => [
              ...state,
              response.data,
            ]);
            addToast({
              type: 'success',
              title: 'Cadastrado',
              description: 'Saída registrada com sucesso.',
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: 'Erro',
              description: error.response.data.message ?? 'Erro ao cadastrar',
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: 'Campos em branco',
          description: err.errors[0],
        });
      });
  }, [date, productId, quantity, observation, setOutputs, close, addToast]);

  const selectedProduct = products.find((p) => p.id === productId);

  return (
    <AModal
      open={open}
      close={close}
      title={
        <>
          <FormOutlined /> Registrar Retirada do Estoque
        </>
      }
      onOk={handleCreate}
      width={620}
    >
      <Form layout="vertical" style={{ maxWidth: '100%' }}>
        <Card size="small" style={{ marginBottom: 12 }}>
          <Row gutter={12}>
            <Col span={24} style={{ marginBottom: 6 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Use este cadastro apenas quando a retirada for manual. Vendas e
                tratamentos podem baixar o estoque automaticamente.
              </Text>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                label={
                  <Space size={6}>
                    <CalendarOutlined style={{ color: '#1890ff' }} />
                    <span>Data da retirada</span>
                  </Space>
                }
              >
                <AInput
                  placeholder="Vazio = hoje"
                  name="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                label={
                  <Space size={6}>
                    <ShoppingOutlined style={{ color: '#faad14' }} />
                    <span>Quantidade retirada</span>
                  </Space>
                }
                required
              >
                <AInput
                  placeholder="Ex: 5"
                  name="quantity"
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(+e.target.value)}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label={
                  <Space size={6}>
                    <ShoppingOutlined style={{ color: '#52c41a' }} />
                    <span>Produto</span>
                  </Space>
                }
                required
              >
                <ASelect
                  showSearch
                  placeholder="Procure por código ou nome"
                  optionFilterProp="children"
                  onChange={(e) => setProductId(e)}
                  filterOption={(input, option: any) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  value={productId}
                  allowClear
                  options={products.map((prod) => ({
                    label: `${prod.code} - ${prod.name}`,
                    value: prod.id,
                  }))}
                />
              </Form.Item>
            </Col>

            {selectedProduct && (
              <Col span={24} style={{ marginTop: -10, marginBottom: 6 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {selectedProduct.name} | Código: {selectedProduct.code} |
                  Unidade: {selectedProduct.unity}
                </Text>
              </Col>
            )}

            {quantity > 0 && (
              <Col span={24} style={{ marginBottom: 6 }}>
                <Row
                  style={{
                    padding: '6px 10px',
                    background: '#fff7e6',
                    border: '1px solid #ffd591',
                    borderRadius: 6,
                  }}
                >
                  <Text style={{ fontSize: 12 }}>
                    Confira se ainda existe saldo suficiente antes de confirmar.
                  </Text>
                </Row>
              </Col>
            )}

            <Col span={24}>
              <Form.Item
                label={
                  <Space size={6}>
                    <FileTextOutlined style={{ color: '#13c2c2' }} />
                    <span>Motivo da retirada</span>
                  </Space>
                }
                style={{ marginBottom: 0 }}
              >
                <AInput
                  placeholder="Ex: perda, uso interno, ajuste manual"
                  name="observation"
                  value={observation}
                  onChange={(e) => setObservation(e.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>
    </AModal>
  );
};

export default InventoryOutputCreate;
